import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { useTranslations } from 'use-intl';
import { Button, Dialog } from 'components';
import { DocumentIcon, DownloadIcon, PlusIcon, TrashIcon } from 'assets/svgs';
import { FirebaseContext, LocalContext } from 'context';
import { formatFileSize } from 'utils';
import { HiddenFileUpload } from 'components/Shared/Header';

const ACCEPTED_FILE_TYPES =
  '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.png,.jpg,.jpeg';

// Factory validation errors carry codes (SessionDocuments) — map them to
// translated Materials keys; anything unrecognised falls back to uploadError.
const UPLOAD_ERROR_KEYS = {
  'file-empty': 'fileEmpty',
  'file-too-large': 'fileTooLarge',
  'file-type-unsupported': 'fileTypeUnsupported'
};

function RelatedMaterials({ sessionId, isModerator, variants }) {
  const t = useTranslations('Materials');
  const tCommon = useTranslations('Common');
  const { firebase, user } = useContext(FirebaseContext);
  const { handleDialog } = useContext(LocalContext);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [documentPendingDeletion, setDocumentPendingDeletion] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const deleteDialogRef = useRef(null);

  useEffect(() => {
    let unsubscribeFromDocuments;
    if (firebase?.sessionDocuments && sessionId) {
      unsubscribeFromDocuments =
        firebase.sessionDocuments.subscribeToSessionDocuments({
          sessionId,
          snapshot: (snapshot) => {
            const _documents = [];
            snapshot.forEach((docSnapshot) => {
              _documents.push(docSnapshot.data());
            });
            setDocuments(_documents);
          }
        });
    }
    return () => {
      if (unsubscribeFromDocuments) {
        unsubscribeFromDocuments();
      }
    };
  }, [firebase, sessionId]);

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setErrorMessage('');
    setUploading(true);
    try {
      await firebase.sessionDocuments.uploadDocument({ file, sessionId, user });
    } catch (error) {
      console.error('Error uploading document:', error);
      setErrorMessage(
        UPLOAD_ERROR_KEYS[error.code]
          ? t(UPLOAD_ERROR_KEYS[error.code])
          : t('uploadError')
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (document) => {
    firebase.sessionDocuments.recordDocumentDownload({
      sessionId,
      documentId: document.documentId,
      fileName: document.fileName,
      user
    });
  };

  const handleDismissDeleteDialog = (e) => {
    e.stopPropagation();
    handleDialog({
      dialogRef: deleteDialogRef,
      animation: 'dismiss',
      stateHandler: () => setDocumentPendingDeletion(null)
    });
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await firebase.sessionDocuments.deleteDocument({
        sessionId,
        document: documentPendingDeletion
      });
      handleDialog({
        dialogRef: deleteDialogRef,
        animation: 'close',
        stateHandler: () => setDocumentPendingDeletion(null),
        callback: () => setDeleting(false)
      });
    } catch (error) {
      console.error('Error deleting document:', error);
      setErrorMessage(t('deleteError'));
      setDeleting(false);
    }
  };

  if (!isModerator && documents.length === 0) {
    return null;
  }

  return (
    <RelatedMaterialsContainer
      key="relatedMaterials"
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="hidden">
      <h3>{t('heading')}</h3>
      {documents.length > 0 && (
        <DocumentList>
          {documents.map((document) => (
            <DocumentRow key={document.documentId}>
              <DocumentLink
                href={document.downloadURL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleDownload(document)}>
                <DocumentIcon width="1.25rem" />
                <DocumentName>{document.fileName}</DocumentName>
                <DocumentSize>{formatFileSize(document.size)}</DocumentSize>
                <DownloadIcon width="1.25rem" />
              </DocumentLink>
              {isModerator && (
                <DeleteButton
                  type="button"
                  aria-label={t('deleteAria', { fileName: document.fileName })}
                  onClick={() => setDocumentPendingDeletion(document)}>
                  <TrashIcon width="1rem" />
                </DeleteButton>
              )}
            </DocumentRow>
          ))}
        </DocumentList>
      )}
      {isModerator && (
        <>
          <Button
            $variant="text"
            icon={<PlusIcon width="1.25rem" />}
            textColor="var(--dark-blue)"
            loading={uploading}
            onClick={() => fileInputRef.current.click()}>
            {t('upload')}
          </Button>
          <HiddenFileUpload
            ref={fileInputRef}
            type="file"
            name="relatedMaterialFile"
            id="relatedMaterialFile"
            accept={ACCEPTED_FILE_TYPES}
            disabled={uploading}
            onChange={handleFileSelected}
          />
        </>
      )}
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      <Dialog
        ref={deleteDialogRef}
        isVisible={Boolean(documentPendingDeletion)}
        onDismiss={handleDismissDeleteDialog}
        contentStyle={{ maxWidth: '454px' }}>
        <DeleteDialogContent>
          <h3>{t('deleteDocument')}</h3>
          <p>
            {t('deleteConfirm', {
              fileName: documentPendingDeletion?.fileName ?? ''
            })}
          </p>
          <DeleteDialogActions>
            <Button
              $variant="outlined"
              border="1px solid var(--dark-blue)"
              onClick={handleDismissDeleteDialog}>
              {tCommon('cancel')}
            </Button>
            <Button
              backgroundColor="var(--red)"
              loading={deleting}
              onClick={handleConfirmDelete}>
              {tCommon('delete')}
            </Button>
          </DeleteDialogActions>
        </DeleteDialogContent>
      </Dialog>
    </RelatedMaterialsContainer>
  );
}

const RelatedMaterialsContainer = styled(motion.div)`
  align-self: flex-start;
  border-top: 1px solid var(--light-grey);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem 0;
  width: 100%;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
  }

  button {
    width: fit-content;

    .loading-spinner {
      left: 0.95em;
    }
  }
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DocumentRow = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;

const DocumentLink = styled.a`
  align-items: center;
  color: var(--dark-blue);
  display: flex;
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  gap: 0.5rem;
  min-width: 0;
  text-decoration: none;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const DocumentName = styled.span`
  /* Grow into the spare space so the size and icons always sit against the
     right edge, whatever the filename's length */
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DocumentSize = styled.span`
  color: var(--light-brown);
  flex-shrink: 0;
  font-size: 0.75rem;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  padding: 0.25rem;
`;

const ErrorMessage = styled.p`
  color: var(--red);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  margin: 0;
`;

const DeleteDialogContent = styled.div`
  background-color: #fff;
  border-radius: var(--border-radius-md);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0 auto;
  padding: 2rem 1.5rem 1.5rem;
  position: relative;
  width: 100%;
  z-index: 1;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
  }

  p {
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    overflow-wrap: anywhere;
  }
`;

const DeleteDialogActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

export default RelatedMaterials;
